/**
 * API Keys Service
 * 
 * This service provides functions to interact with the API Keys API endpoints.
 */
import httpClient from "./http.client"
import store from "@/store"

/**
 * Get a list of API keys with optional filtering
 * 
 * @param {Object} params - Query parameters for filtering/searching API keys
 * @returns {Promise} - Promise resolving to the API keys data
 */
export function listApiKeys(params = {}) {
  return httpClient.get(`/api/organizations/${store.state.organization.id}/api-keys/`, { params })
    .then(response => response.data)
}

/**
 * Create a new API key
 *
 * @param {Object} keyData - Data for the new API key
 * @returns {Promise} - Promise resolving to the created API key data
 */
export function createApiKey(keyData) {
  // Filter data to only include allowed fields (organization and user are auto-detected)
  const filteredData = {
    name: keyData.name,
    scopes: keyData.scopes,
    is_service_account: keyData.is_service_account,
    enabled: keyData.enabled !== undefined ? keyData.enabled : true
  }
  
  // Remove any undefined values
  Object.keys(filteredData).forEach(key => {
    if (filteredData[key] === undefined) {
      delete filteredData[key]
    }
  })
  
  return httpClient.post(`/api/organizations/${store.state.organization.id}/api-keys/`, filteredData)
    .then(response => response.data)
}

/**
 * Get a specific API key by ID
 * 
 * @param {string|number} keyId - ID of the API key to retrieve
 * @returns {Promise} - Promise resolving to the API key data
 */
export function getApiKey(keyId) {
  return httpClient.get(`/api/organizations/${store.state.organization.id}/api-keys/${keyId}/`)
    .then(response => response.data)
}

/**
 * Update an API key (full update)
 *
 * @param {string|number} keyId - ID of the API key to update
 * @param {Object} keyData - Updated API key data
 * @returns {Promise} - Promise resolving to the updated API key data
 */
export function updateApiKey(keyId, keyData) {
  // Filter data to only include allowed fields (organization and user are auto-detected)
  const filteredData = {
    name: keyData.name,
    scopes: keyData.scopes,
    is_service_account: keyData.is_service_account,
    enabled: keyData.enabled
  }
  
  // Remove any undefined values
  Object.keys(filteredData).forEach(key => {
    if (filteredData[key] === undefined) {
      delete filteredData[key]
    }
  })
  
  return httpClient.put(`/api/organizations/${store.state.organization.id}/api-keys/${keyId}/`, filteredData)
    .then(response => response.data)
}

/**
 * Revoke an API key
 * 
 * @param {string|number} keyId - ID of the API key to revoke
 * @returns {Promise} - Promise resolving when revocation is complete
 */
export function revokeApiKey(keyId) {
  return httpClient.delete(`/api/organizations/${store.state.organization.id}/api-keys/${keyId}/`)
    .then(response => response.data)
}

/**
 * Regenerate an API key
 *
 * @param {string|number} keyId - ID of the API key to regenerate
 * @returns {Promise} - Promise resolving to the regenerated API key data (includes new secret key)
 */
export function regenerateApiKey(keyId) {
  return httpClient.post(`/api/organizations/${store.state.organization.id}/api-keys/${keyId}/regenerate/`, {})
    .then(response => response.data)
}

/**
 * Enable an API key
 *
 * @param {string|number} keyId - ID of the API key to enable
 * @returns {Promise} - Promise resolving to the updated API key data
 */
export function enableApiKey(keyId) {
  return httpClient.post(`/api/organizations/${store.state.organization.id}/api-keys/${keyId}/enable/`, {})
    .then(response => response.data)
}

/**
 * Disable an API key
 *
 * @param {string|number} keyId - ID of the API key to disable
 * @returns {Promise} - Promise resolving to the updated API key data
 */
export function disableApiKey(keyId) {
  return httpClient.post(`/api/organizations/${store.state.organization.id}/api-keys/${keyId}/disable/`, {})
    .then(response => response.data)
}

/**
 * Get usage statistics for an API key
 * 
 * @param {string|number} keyId - ID of the API key to get usage for
 * @param {Object} params - Query parameters for filtering usage data
 * @returns {Promise} - Promise resolving to the usage statistics
 */
export function getApiKeyUsage(keyId, params = {}) {
  return httpClient.get(`/api/organizations/${store.state.organization.id}/api-keys/${keyId}/usage/`, { params })
    .then(response => response.data)
}

/**
 * Get available scopes for API keys
 * 
 * @returns {Promise} - Promise resolving to the available scopes
 */
export function getAvailableScopes() {
  return httpClient.get(`/api/organizations/${store.state.organization.id}/api-keys/scopes/`)
    .then(response => response.data)
}

/**
 * Get available API resources
 * 
 * @returns {Promise} - Promise resolving to the available API resources
 */
export function getApiResources() {
  return httpClient.get(`/api/organizations/${store.state.organization.id}/api-keys/resources/`)
    .then(response => response.data)
}

/**
 * Get accessible endpoints for API keys
 * 
 * @returns {Promise} - Promise resolving to the accessible endpoints
 */
export function getAccessibleEndpoints() {
  return httpClient.get(`/api/organizations/${store.state.organization.id}/api-keys/accessible-endpoints/`)
    .then(response => response.data)
}

/**
 * API Keys service module that exports all functions
 */
export default {
  listApiKeys,           // Corresponds to List API Keys (GET /)
  createApiKey,          // Corresponds to Create API Key (POST /)
  getApiKey,             // Corresponds to Get API Key Details (GET /{key_id}/)
  updateApiKey,          // Corresponds to Update API Key (PUT /{key_id}/)
  revokeApiKey,          // Corresponds to Revoke API Key (DELETE /{key_id}/)
  regenerateApiKey,      // Corresponds to Regenerate API Key (POST /{key_id}/regenerate/)
  enableApiKey,          // Corresponds to Enable API Key (POST /{key_id}/enable/)
  disableApiKey,         // Corresponds to Disable API Key (POST /{key_id}/disable/)
  getApiKeyUsage,        // Corresponds to Get Usage Statistics (GET /{key_id}/usage/)
  getAvailableScopes,    // Corresponds to Get Available Scopes (GET /scopes/)
  getApiResources,       // Corresponds to Get API Resources (GET /resources/)
  getAccessibleEndpoints // Corresponds to Get Accessible Endpoints (GET /accessible-endpoints/)
}
